import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkle, ArrowRight, X } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ADMIN_ROLES = new Set(["super_admin", "company_admin", "hr"]);

/**
 * Dismissible banner that nudges admins to run the AI Setup Wizard until
 * the tenant's setup is marked complete on the backend.
 *
 * - Only rendered for admin roles (super_admin, company_admin, hr).
 * - super_admin without a selected tenant sees nothing (agency-wide view).
 * - Dismissal is per-session per-tenant via sessionStorage.
 * - Reappears on next login (fresh session) until backend reports completed.
 */
export default function SetupBanner() {
  const { user, tenantId } = useAuth();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const scopeKey = user?.role === "super_admin" ? tenantId : user?.tenant_id;

  useEffect(() => {
    if (!user || !ADMIN_ROLES.has(user.role)) { setStatus(null); return; }
    if (user.role === "super_admin" && !tenantId) { setStatus(null); return; }
    const params = user.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
    api.get("/admin/setup-wizard/status", { params })
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus(null));
    // Reset per-tenant dismissal check whenever scope changes.
    if (scopeKey) {
      const key = `zenith360-setup-banner-dismissed-${scopeKey}`;
      setDismissed(sessionStorage.getItem(key) === "1");
    } else {
      setDismissed(false);
    }
  }, [user?.id, user?.role, user?.tenant_id, tenantId, scopeKey]);

  if (!user || !ADMIN_ROLES.has(user.role)) return null;
  if (user.role === "super_admin" && !tenantId) return null;
  if (!status || status.setup_completed) return null;
  if (dismissed) return null;

  const onDismiss = () => {
    if (scopeKey) sessionStorage.setItem(`zenith360-setup-banner-dismissed-${scopeKey}`, "1");
    setDismissed(true);
  };

  return (
    <div
      data-testid="setup-wizard-banner"
      className="relative overflow-hidden rounded-md border border-zenith-teal/30 bg-gradient-to-r from-zenith-teal/10 via-zenith-magenta/5 to-zenith-green/10 px-4 py-3 mb-4 flex items-center gap-3 flex-wrap"
    >
      <div className="absolute -left-8 -top-8 w-24 h-24 rounded-full bg-zenith-teal/20 blur-2xl pointer-events-none" />
      <div className="absolute -right-8 -bottom-8 w-24 h-24 rounded-full bg-zenith-magenta/20 blur-2xl pointer-events-none" />

      <div className="relative w-9 h-9 shrink-0 rounded-md bg-white grid place-items-center border border-zenith-teal/30">
        <Sparkle size={18} weight="fill" className="text-zenith-magenta" />
      </div>
      <div className="relative flex-1 min-w-[240px]">
        <div className="text-sm font-display font-bold text-adb-navy leading-tight">
          Finish setting up your Zenith360 workspace
        </div>
        <div className="text-[11px] text-zinc-600 mt-0.5">
          Let AI draft your policies, grade &amp; band structure, salary split, holidays and joining documents — in about a minute.
        </div>
      </div>
      <Link
        to="/app/admin"
        data-testid="setup-wizard-banner-launch"
        className="relative inline-flex items-center gap-1.5 rounded-sm bg-adb-navy text-white text-xs font-semibold px-3 py-2 hover:bg-adb-navy-800 transition-colors"
      >
        <Sparkle size={12} weight="fill" className="text-zenith-green" />
        Launch AI Setup Wizard
        <ArrowRight size={12} />
      </Link>
      <button
        type="button"
        onClick={onDismiss}
        data-testid="setup-wizard-banner-dismiss"
        aria-label="Dismiss setup banner"
        className="relative p-1.5 rounded-sm text-zinc-500 hover:text-adb-navy hover:bg-white/60 transition-colors"
      >
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}
